import axios from "axios";
import { logger } from "./logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// One-time cleanup: purge any legacy access_token that previous builds
// persisted to localStorage. Tokens now live in memory only (OWASP-recommended
// pattern) so an XSS payload cannot exfiltrate a long-lived credential.
// Wrapped in try/catch because sandboxed iframes throw on localStorage access.
try {
  window.localStorage.removeItem("cs.access_token");
} catch {
  /* sandboxed iframe or storage disabled — nothing to purge */
}

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
  // Axios reads `document.cookie` to forward the XSRF token when the request
  // is same-origin. Inside a sandboxed iframe that throws
  // "Failed to read the 'cookie' property from 'Document'". We don't use
  // XSRF cookies anywhere in this app (auth is bearer + httpOnly cookies),
  // so it's safe to disable that path entirely.
  withXSRFToken: false,
  xsrfCookieName: null,
});

// Access token is kept in memory ONLY. Refresh token lives in an HttpOnly,
// Secure, SameSite=None cookie set by the backend. On page reload the token
// is re-hydrated lazily: the first request without a Bearer header still ships
// the access_token cookie (backend accepts either); if that cookie has already
// expired the response interceptor below calls `/auth/refresh` (using the
// HttpOnly refresh cookie) and retries the original request transparently.
let inMemoryToken = null;
export function setAccessToken(token) {
  inMemoryToken = token || null;
}
export function getAccessToken() {
  return inMemoryToken;
}

http.interceptors.request.use((cfg) => {
  const t = getAccessToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401 && !err.config?._retried && !err.config?.url?.endsWith("/auth/login")) {
      err.config._retried = true;
      try {
        const r = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        if (r.data?.access_token) {
          setAccessToken(r.data.access_token);
          err.config.headers.Authorization = `Bearer ${r.data.access_token}`;
          return http(err.config);
        }
      } catch (e) {
        // Refresh token request failed (no refresh cookie / expired) — fall through
        // to the original 401 so calling code can route the user to /login.
        logger.debug("Token refresh failed:", e?.response?.status || e?.message);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth
export const apiLogin = (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data);
export const apiRegister = (payload) => http.post("/auth/register", payload).then((r) => r.data);
export const apiLogout = () => http.post("/auth/logout").then((r) => r.data);
export const apiMe = () => http.get("/auth/me").then((r) => r.data);

// Projects
export const listProjects = () => http.get("/projects").then((r) => r.data);
export const createProject = (payload) => http.post("/projects", payload).then((r) => r.data);
export const getProject = (id) => http.get(`/projects/${id}`).then((r) => r.data);
export const patchProject = (id, payload) => http.patch(`/projects/${id}`, payload).then((r) => r.data);
export const deleteProject = (id) => http.delete(`/projects/${id}`).then((r) => r.data);
export const getSetupStatus = (id) => http.get(`/projects/${id}/setup-status`).then((r) => r.data);

// Companies / Users
export const getMyCompany = () => http.get("/companies/mine").then((r) => r.data);
export const patchMyCompany = (payload) => http.patch("/companies/mine", payload).then((r) => r.data);
export const listAllCompanies = () => http.get("/companies/all").then((r) => r.data);
export const listUsers = () => http.get("/users").then((r) => r.data);
export const inviteUser = (payload) => http.post("/users", payload).then((r) => r.data);
export const updateUser = (id, payload) => http.patch(`/users/${id}`, payload).then((r) => r.data);
export const deleteUser = (id) => http.delete(`/users/${id}`).then((r) => r.data);
export const listUserCompanies = (id) => http.get(`/users/${id}/companies`).then((r) => r.data);
export const addUserCompany = (id, payload) => http.post(`/users/${id}/companies`, payload).then((r) => r.data);
export const removeUserCompany = (id, companyId) => http.delete(`/users/${id}/companies/${companyId}`).then((r) => r.data);

// Share links
export const updateShareLink = (id, payload) => http.patch(`/share-links/${id}`, payload).then((r) => r.data);

// SharePoint - per-project
export const getSharePointConfig = (projectId) => http.get(`/projects/${projectId}/sharepoint`).then((r) => r.data);
export const upsertSharePointConfig = (projectId, payload) => http.put(`/projects/${projectId}/sharepoint`, payload).then((r) => r.data);
export const deleteSharePointConfig = (projectId) => http.delete(`/projects/${projectId}/sharepoint`).then((r) => r.data);
export const testSharePointConnection = (projectId) => http.post(`/projects/${projectId}/sharepoint/test`).then((r) => r.data);
export const listSharePointFiles = (projectId, payload = {}) => http.post(`/projects/${projectId}/sharepoint/files`, payload).then((r) => r.data);
export const importSharePointFiles = (projectId, files) => http.post(`/projects/${projectId}/sharepoint/import`, { files }).then((r) => r.data);
export const previewSharePointUrl = (library_url) => http.post(`/sharepoint/preview-url`, { library_url }).then((r) => r.data);
export const syncSharePointNow = (projectId) => http.post(`/projects/${projectId}/sharepoint/sync-now`).then((r) => r.data);
export const getSharePointSyncStatus = (projectId) => http.get(`/projects/${projectId}/sharepoint/sync-status`).then((r) => r.data);

// SharePoint - company-level tenant credentials
export const getCompanySpCreds = () => http.get(`/company/sharepoint-credentials`).then((r) => r.data);
export const upsertCompanySpCreds = (payload) => http.put(`/company/sharepoint-credentials`, payload).then((r) => r.data);
export const deleteCompanySpCreds = () => http.delete(`/company/sharepoint-credentials`).then((r) => r.data);
export const testCompanySpCreds = () => http.post(`/company/sharepoint-credentials/test`).then((r) => r.data);

// Syncs
export const listSyncs = (projectId) => http.get(`/projects/${projectId}/syncs`).then((r) => r.data);
export const getSync = (id) => http.get(`/syncs/${id}`).then((r) => r.data);
export const uploadSync = (projectId, formData, opts) => http.post(`/projects/${projectId}/syncs/upload`, formData, { headers: { "Content-Type": "multipart/form-data" }, ...opts }).then((r) => r.data);
export const patchSync = (id, payload) => http.patch(`/syncs/${id}`, payload).then((r) => r.data);
export const reparseSync = (id) => http.post(`/syncs/${id}/reparse`).then((r) => r.data);
export const reparseEmptySyncs = (projectId) => http.post(`/projects/${projectId}/syncs/reparse-empty`).then((r) => r.data);
export const getIncompleteSyncsStats = (projectId) => http.get(`/projects/${projectId}/syncs/incomplete-stats`).then((r) => r.data);
// iter43: Data Explorer — flat cross-project table for admins.
export const listDataShareLinks = () => http.get(`/data/share-links`).then((r) => r.data);
export const listDataProjects = () => http.get(`/data/projects`).then((r) => r.data);
export const getDataTasks = (params = {}) => http.get(`/data/tasks`, { params }).then((r) => r.data);
export const getDataTasksCsvUrl = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `${BACKEND_URL}/api/data/tasks.csv${qs ? "?" + qs : ""}`;
};
// Accepts { company_id?, project_id?, only_incomplete?, use_sharepoint_fallback?, max_syncs? }
export const reparseEverything = (payload = {}) => http.post(`/admin/reparse-everything`, payload).then((r) => r.data);
export const getProjectDiagnostics = () => http.get(`/admin/diagnostics/projects`).then((r) => r.data);
export const deleteSync = (id) => http.delete(`/syncs/${id}`).then((r) => r.data);
export const compareSyncs = (base, target) => http.get(`/syncs/compare`, { params: { base, target } }).then((r) => r.data);

// Tasks/views
export const getGantt = (syncId) => http.get(`/syncs/${syncId}/gantt`).then((r) => r.data);
export const getKanban = (syncId) => http.get(`/syncs/${syncId}/kanban`).then((r) => r.data);
export const getLineOfBalance = (syncId) => http.get(`/syncs/${syncId}/line-of-balance`).then((r) => r.data);

// Restrictions
export const listRestrictions = (projectId) => http.get(`/projects/${projectId}/restrictions`).then((r) => r.data);
export const createRestriction = (projectId, payload) => http.post(`/projects/${projectId}/restrictions`, payload).then((r) => r.data);
export const patchRestriction = (id, payload) => http.patch(`/restrictions/${id}`, payload).then((r) => r.data);
export const deleteRestriction = (id) => http.delete(`/restrictions/${id}`).then((r) => r.data);

// AI
export const analyzeSchedule = (payload) => http.post("/ai/schedule/analyze", payload).then((r) => r.data);
export const compareAnalyzeSchedule = (payload) => http.post("/ai/schedule/compare-analyze", payload).then((r) => r.data);
export const getAiHistory = (projectId) => http.get(`/projects/${projectId}/ai/history`).then((r) => r.data);

// Admin maintenance — recompute the `seq` field on every task in the chosen
// scope. Pass `{ all_companies: true }` (super-admin only) to migrate every
// tenant in one call; otherwise it defaults to the caller's active company.
export const recomputeTaskSeq = (payload = {}) =>
  http.post("/admin/recompute-task-seq", payload).then((r) => r.data);

